<template>
  <b-button
    variant="outline-primary"
    size="sm"
    :title="isDisabled ? disabledTooltip : computedTitle"
    :disabled="isDisabled"
    @click="generateAndEmit"
  >
    <feather-icon
      icon="ZapIcon"
      class="mr-50"
    />
    {{ computedButtonText }}
  </b-button>
</template>

<script>
/**
 * PasswordGenerator Component
 *
 * Reusable component for generating secure, memorable passwords based on user data.
 * IMPORTANT: Never includes company/brand names in generated passwords.
 *
 * Usage:
 * 1. With form data (Create/Edit pages):
 *    <PasswordGenerator
 *      :form-data="formData"
 *      user-type="manager"
 *      @generated="onPasswordGenerated"
 *    />
 *
 * 2. With user object (Detail pages):
 *    <PasswordGenerator
 *      :user="selectedUser"
 *      user-type="supplier"
 *      @generated="onPasswordGenerated"
 *    />
 *
 * 3. Auto-fill mode:
 *    <PasswordGenerator
 *      :form-data="formData"
 *      user-type="deliveryman"
 *      auto-fill
 *      @generated="onPasswordGenerated"
 *    />
 */

export default {
  name: 'PasswordGenerator',
  props: {
    // For forms with reactive data
    formData: {
      type: Object,
      default: null,
    },
    // For detail pages with existing user
    user: {
      type: Object,
      default: null,
    },
    // Type of user for contextual password generation
    userType: {
      type: String,
      required: true,
      validator: v => ['manager', 'supplier', 'deliveryman', 'staff', 'pickupeur'].includes(v),
    },
    // Auto-fill password and confirm_password fields in formData
    autoFill: {
      type: Boolean,
      default: false,
    },
    // Custom button text
    buttonText: {
      type: String,
      default: null,
    },
    // Custom button title (tooltip)
    buttonTitle: {
      type: String,
      default: null,
    },
  },
  computed: {
    /**
     * Check if button should be disabled due to insufficient data
     */
    isDisabled() {
      const userData = this.user || this.formData
      if (!userData) return true

      const username = userData.username || userData.name || ''
      const email = userData.email || ''
      const phone = userData.phone || ''

      // Need at least username OR (email + phone) to generate a meaningful password
      const hasUsername = username && username.trim().length > 0
      const hasEmail = email && email.includes('@')
      const hasPhone = phone && phone.replace(/\D/g, '').length >= 4

      // Disable if we don't have enough data
      return !hasUsername && !(hasEmail && hasPhone)
    },

    /**
     * Tooltip to show when button is disabled
     */
    disabledTooltip() {
      return 'Veuillez d\'abord renseigner le nom, ou l\'email et le téléphone pour générer un mot de passe'
    },

    computedButtonText() {
      if (this.buttonText) return this.buttonText
      return 'Générer un mot de passe unique'
    },
    computedTitle() {
      if (this.buttonTitle) return this.buttonTitle

      const userTypeLabels = {
        manager: 'du responsable',
        supplier: 'du fournisseur',
        deliveryman: 'du livreur',
        staff: 'du membre',
        pickupeur: 'du ramasseur',
      }

      const label = userTypeLabels[this.userType] || 'de l\'utilisateur'
      return `Générer un mot de passe unique basé sur les informations ${label}`
    },
  },
  methods: {
    generateAndEmit() {
      const userData = this.user || this.formData

      if (!userData) {
        this.$toast({
          component: 'ToastificationContent',
          props: {
            title: 'Erreur',
            text: 'Aucune donnée utilisateur disponible pour générer un mot de passe',
            icon: 'AlertCircleIcon',
            variant: 'danger',
          },
        })
        return
      }

      const password = this.generateMemorablePassword(userData)

      // Emit to parent
      this.$emit('generated', password)

      // Optional: auto-fill if enabled and formData provided
      if (this.autoFill && this.formData) {
        this.$set(this.formData, 'password', password)
        this.$set(this.formData, 'confirm_password', password)
      }

      this.$toast({
        component: 'ToastificationContent',
        props: {
          title: 'Mot de passe généré',
          text: 'Un mot de passe unique a été généré avec succès',
          icon: 'CheckCircleIcon',
          variant: 'success',
        },
      })
    },

    /**
     * Generate a memorable password from user data
     * Uses: name, email, phone, ID - NEVER uses company/brand names
     */
    generateMemorablePassword(userData) {
      // Extract user info
      const username = userData.username || userData.name || 'User'
      const email = userData.email || ''
      const phone = userData.phone || ''
      const userId = userData.id || ''

      // Clean and format name
      const rawName = username.trim().split(' ')[0]
      const cleanName = this.stripAccents(rawName)
      const capitalizedName = cleanName.charAt(0).toUpperCase() + cleanName.slice(1).toLowerCase()

      // Extract email parts
      const emailUsername = email.includes('@') ? this.stripAccents(email.split('@')[0]) : ''
      const emailDomain = email.includes('@') ? email.split('@')[1].split('.')[0] : ''

      // Extract phone digits
      const phoneDigits = phone.replace(/\D/g, '').slice(-4)

      // Special characters pool
      const specialChars = ['@', '*', '#', '!', '$', '&']
      const randomSpecial1 = specialChars[Math.floor(Math.random() * specialChars.length)]
      const randomSpecial2 = specialChars[Math.floor(Math.random() * specialChars.length)]

      const currentYear = new Date().getFullYear()

      // Generate variations
      const variations = []

      // Variation 1: Name + Phone + Special
      if (phoneDigits && phoneDigits.length >= 4) {
        variations.push(`${capitalizedName}${phoneDigits}${randomSpecial1}`)
      }

      // Variation 2: Name + Special + Domain + Number
      if (emailDomain) {
        const domainCapital = emailDomain.charAt(0).toUpperCase() + emailDomain.slice(1)
        variations.push(`${capitalizedName}${randomSpecial1}${domainCapital}1`)
      }

      // Variation 3: Email Username + Special + Phone + Special
      if (emailUsername && phoneDigits) {
        const emailCap = emailUsername.charAt(0).toUpperCase() + emailUsername.slice(1)
        variations.push(`${emailCap}${randomSpecial1}${phoneDigits}${randomSpecial2}`)
      }

      // Variation 4: Name + Special + Year
      variations.push(`${capitalizedName}${randomSpecial1}${currentYear}`)

      // Variation 5: Name + Year + Phone + Special
      if (phoneDigits) {
        variations.push(`${capitalizedName}${currentYear}${phoneDigits}${randomSpecial1}`)
      }

      // Variation 6: Name + Special + Year + Special
      variations.push(`${capitalizedName}${randomSpecial1}${currentYear}${randomSpecial2}`)

      // Variation 7: Name + ID + Special
      if (userId) {
        variations.push(`${capitalizedName}${userId}${randomSpecial1}`)
      }

      // Filter valid variations (min 8 chars)
      const validVariations = variations.filter(v => v && v.length >= 8)

      if (validVariations.length === 0) {
        // Fallback: generic strong password
        return this.generateFallbackPassword()
      }

      // Return random valid variation
      return validVariations[Math.floor(Math.random() * validVariations.length)]
    },

    /**
     * Remove accents from string
     */
    stripAccents(str) {
      return str.normalize('NFD').replace(/[\u0300-\u036f]/g, '')
    },

    /**
     * Generate a secure fallback password when user data is insufficient
     */
    generateFallbackPassword() {
      const chars = 'ABCDEFGHIJKLMNOPQRSTUVWXYZabcdefghijklmnopqrstuvwxyz0123456789!@#$%^&*'
      const upper = 'ABCDEFGHIJKLMNOPQRSTUVWXYZ'
      const lower = 'abcdefghijklmnopqrstuvwxyz'
      const digits = '0123456789'
      const special = '!@#$%^&*'

      let password = ''
      // Ensure at least one of each type
      password += upper.charAt(Math.floor(Math.random() * upper.length))
      password += lower.charAt(Math.floor(Math.random() * lower.length))
      password += digits.charAt(Math.floor(Math.random() * digits.length))
      password += special.charAt(Math.floor(Math.random() * special.length))

      // Fill remaining with random chars
      for (let i = 0; i < 8; i += 1) {
        password += chars.charAt(Math.floor(Math.random() * chars.length))
      }

      // Shuffle the password
      return password.split('').sort(() => 0.5 - Math.random()).join('')
    },
  },
}
</script>

<style scoped>
/* Disabled button styling */
button:disabled {
  cursor: not-allowed !important;
  opacity: 0.6;
}
</style>
